import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Alert, Grid, CircularProgress,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  CheckCircle as DoneIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { publicAPI } from '../../services/api';

// Public Master defense request form — استمارة طلب المناقشة الخاصة بالماستر (no login)

// Must match MASTERS in masterform-routes.js (the server rejects anything else)
const MASTERS = [
  'Gouvernance sécuritaire et ingénierie cybernétique',
  'Droit et pratiques judiciaires',
  'قانون الإعلام والاتصال',
  'القانون المدني وتقنيات التوثيق والرقمنة',
  'العلوم الجنائية والتعاون الجنائي الدولي',
  'التدبير الإداري والمالي للطلبيات العمومية',
  'القانون والمنازعات',
  'الإدارة والتعمير',
  'Droit et sécurité numérique',
  'التهيئة والعقار والاستثمار',
  'العلوم الجنائية والأمنية',
  'المقاولة والقانون',
  'القانون والتأمين',
];

const CURRENT_YEAR = new Date().getFullYear();
const REGISTRATION_YEARS = Array.from({ length: 7 }, (_, i) => String(CURRENT_YEAR - i));

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const DOCUMENTS = [
  { name: 'cin_file',            label: 'نسخة من البطاقة الوطنية / Copie de la CIN' },
  { name: 'releve_note_s1_file', label: 'كشف النقط - الفصل الأول / Relevé de Notes S1' },
  { name: 'releve_note_s2_file', label: 'كشف النقط - الفصل الثاني / Relevé de Notes S2' },
  { name: 'releve_note_s3_file', label: 'كشف النقط - الفصل الثالث / Relevé de Notes S3' },
];

const EMPTY = {
  full_name: '', master_name: '', national_student_id: '', apogee_number: '',
  national_card_id: '', phone: '', birth_date: '', birth_place: '', license_obtained: '',
  supervisor_name: '', research_title: '', registration_date: '', discussion_date: '', email: '', notes: '',
};

const MasterForm = () => {
  const [values, setValues]       = useState(EMPTY);
  const [files, setFiles]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [error, setError]         = useState(null);

  const set = (name) => (e) => setValues(v => ({ ...v, [name]: e.target.value }));

  const pickFile = (name) => (e) => {
    const file = e.target.files?.[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setError('حجم الملف يجب أن لا يتجاوز 5 ميغابايت / La taille du fichier ne doit pas dépasser 5 Mo');
      e.target.value = '';
      return;
    }
    setError(null);
    setFiles(f => ({ ...f, [name]: file || undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (DOCUMENTS.some(d => !files[d.name])) {
      setError('يرجى إرفاق جميع المستندات المطلوبة / Veuillez joindre tous les documents requis');
      return;
    }
    const data = new FormData();
    Object.entries(values).forEach(([k, v]) => data.append(k, v));
    DOCUMENTS.forEach(d => data.append(d.name, files[d.name]));

    setSubmitting(true); setError(null);
    try {
      await publicAPI.submitMasterForm(data);
      setSuccess(true);
      setValues(EMPTY); setFiles({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ، يرجى المحاولة مرة أخرى / Une erreur est survenue, veuillez réessayer');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const text = (name, label, props = {}) => (
    <TextField
      fullWidth required label={label} value={values[name]} onChange={set(name)}
      InputLabelProps={props.type === 'date' ? { shrink: true } : undefined}
      {...props}
    />
  );

  return (
    <Box dir="rtl" sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', py: { xs: 2, md: 5 }, px: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 860, mx: 'auto', borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0' }}>

        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
          color: 'white', p: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', gap: 2.5,
          flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'right' },
        }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'white', p: 1, flexShrink: 0 }}>
            <Box component="img" src="/logo1.png" alt="FSJP" sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} lineHeight={1.4}>استمارة طلب المناقشة الخاصة بالماستر</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              Demande de soutenance — Master · يرجى ملء جميع البيانات المطلوبة بدقة
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2.5, md: 4 } }}>
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(false)}>
              تم تسجيل طلبك بنجاح! شكراً لك. / Votre demande a été enregistrée avec succès.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>{text('full_name', 'الأسم والنسب / Nom et Prénom')}</Grid>
            <Grid item xs={12} md={6}>
              {text('master_name', 'اسم الماستر / Nom du Master', {
                select: true,
                children: MASTERS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>),
              })}
            </Grid>

            <Grid item xs={12} md={6}>{text('national_student_id', 'الرقم الوطني للطالب / N° National Étudiant (CNE)')}</Grid>
            <Grid item xs={12} md={6}>{text('apogee_number', 'رقم ابوجي / N° Apogée')}</Grid>
            <Grid item xs={12} md={6}>{text('national_card_id', 'رقم البطاقة الوطنية / N° CIN')}</Grid>
            <Grid item xs={12} md={6}>{text('phone', 'رقم الهاتف / N° Téléphone', { type: 'tel' })}</Grid>
            <Grid item xs={12} md={6}>{text('birth_date', 'تاريخ الإزدياد / Date de Naissance', { type: 'date' })}</Grid>
            <Grid item xs={12} md={6}>{text('birth_place', 'مكان الإزدياد / Lieu de Naissance')}</Grid>
            <Grid item xs={12}>{text('license_obtained', 'الإجازة المحصل عليها / Licence Obtenue')}</Grid>
            <Grid item xs={12}>{text('supervisor_name', 'اسم الاستاذ المؤطر / Nom du Professeur Encadrant')}</Grid>
            <Grid item xs={12}>{text('research_title', 'عنوان البحث / Titre de Recherche', { multiline: true, minRows: 3 })}</Grid>
            <Grid item xs={12} md={6}>
              {text('registration_date', "سنة التسجيل / Année d'Inscription", {
                select: true,
                children: REGISTRATION_YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>),
              })}
            </Grid>
            <Grid item xs={12} md={6}>{text('discussion_date', 'تاريخ المناقشة / Date de Soutenance', { type: 'date' })}</Grid>
            <Grid item xs={12}>{text('email', 'البريد الالكتروني / Email (facultatif)', { type: 'email', required: false })}</Grid>
            <Grid item xs={12}>
              {text('notes', 'ملاحظات اضافية / Notes Supplémentaires (facultatif)', { multiline: true, minRows: 3, required: false })}
            </Grid>

            {/* Documents */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#2c3e50' }}>المستندات المطلوبة / Documents Requis *</Typography>
              <Typography variant="body2" color="text.secondary">
                PDF أو صورة (JPG/PNG)، بحجم لا يتجاوز 5 ميغابايت لكل ملف · PDF ou image (JPG/PNG), 5 Mo max. par fichier
              </Typography>
            </Grid>
            {DOCUMENTS.map(d => {
              const file = files[d.name];
              return (
                <Grid item xs={12} md={6} key={d.name}>
                  <Button
                    component="label" fullWidth variant="outlined"
                    color={file ? 'success' : 'primary'}
                    startIcon={file ? <DoneIcon /> : <UploadIcon />}
                    sx={{ justifyContent: 'flex-start', textAlign: 'right', borderRadius: 2, py: 1.5, borderStyle: file ? 'solid' : 'dashed', textTransform: 'none', gap: 1 }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={700}>{d.label} *</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {file ? file.name : 'اختر ملفاً / Choisir un fichier'}
                      </Typography>
                    </Box>
                    <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={pickFile(d.name)} />
                  </Button>
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <Button
                type="submit" fullWidth variant="contained" size="large" disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ borderRadius: 3, py: 1.5, fontSize: '1.05rem', fontWeight: 700, boxShadow: 'none', bgcolor: '#2c3e50', '&:hover': { bgcolor: '#1a2a3a' }, gap: 1 }}
              >
                إرسال الطلب / Envoyer la Demande
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default MasterForm;
